import axios, {isAxiosError} from 'axios'

export const TOKEN_KEY = 'sst_token'

/** Variable de módulo para que el interceptor lea el valor más reciente. El backend solo lo
 * respeta para usuarios de sistema y en lecturas. */
const VIEW_AS_HEADER = 'X-View-As-Employee'
let viewAsEmployeeId: string | null = null
export function setViewAsEmployeeId(id: string | null): void {
  viewAsEmployeeId = id
}

/** Cruza empresas: no depende de una ruta de empresa. El backend solo lo respeta para usuarios
 * de sistema y en lecturas. */
const VIEW_AS_PARTNER_HEADER = 'X-View-As-Partner'
let viewAsPartnerId: string | null = null
export function setViewAsPartnerId(id: string | null): void {
  viewAsPartnerId = id
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  if (viewAsEmployeeId) {
    config.headers[VIEW_AS_HEADER] = viewAsEmployeeId
  }
  if (viewAsPartnerId) {
    config.headers[VIEW_AS_PARTNER_HEADER] = viewAsPartnerId
  }
  return config
})

/** Aquí un 401 significa «credencial equivocada», no «sesión vencida»: no hay sesión que cerrar. */
const RUTAS_DE_CREDENCIAL = ['/auth/login', '/auth/google', '/auth/me/password']

let alVencerLaSesion: (() => void) | null = null

/** Vive fuera de React porque quien detecta el 401 es el interceptor de axios. */
export function registrarVencimientoDeSesion(manejar: (() => void) | null): void {
  alVencerLaSesion = manejar
}

function esSesionNoReconocida(error: unknown): boolean {
  if (!isAxiosError(error) || error.response?.status !== 401) return false
  const ruta = error.config?.url ?? ''
  return !RUTAS_DE_CREDENCIAL.some((credencial) => ruta.startsWith(credencial))
}

/** Un 401 en una lectura cierra la sesión: `ProtectedRoute` mira `user` en memoria y no se
 * entera si el token desaparece. */
api.interceptors.response.use(null, (error: unknown) => {
  if (esSesionNoReconocida(error)) alVencerLaSesion?.()
  return Promise.reject(error)
})

/** Con `responseType: 'blob'` axios entrega también el JSON del error como `Blob`: se relee para
 * que `translateApiError` encuentre el `detail`. */
async function conDetalleLegible(error: unknown): Promise<unknown> {
  const respuesta = (error as {response?: {data?: unknown}}).response
  if (!(respuesta?.data instanceof Blob)) return error
  try {
    respuesta.data = JSON.parse(await respuesta.data.text())
  } catch {
    // no era JSON: se deja el blob y el llamador ve el mensaje genérico.
  }
  return error
}

/** Descarga vía blob: un enlace directo no puede llevar el token bearer. */
export async function downloadBlob(url: string, filename: string): Promise<void> {
  let resp
  try {
    resp = await api.get(url, {responseType: 'blob'})
  } catch (error) {
    throw await conDetalleLegible(error)
  }
  const objectUrl = URL.createObjectURL(resp.data as Blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = filename
  link.click()
  URL.revokeObjectURL(objectUrl)
}
